// ============================================================
// PurchaseOrdersTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
//   • PO list with status and receiving progress
//   • Create / edit form with dynamic line items
//   • Detail modal showing every line and what's still pending
//
// A PO is a header (vendor, date) plus lines (jumbo, qty, rate).
// Lines are held in form state as an array — add a row, fill it,
// remove it — and sent to the backend in one request.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. StatusBadge
//   C. LineItemsEditor  — the dynamic rows
//   D. PoForm
//   E. PoDetailModal
//   F. PurchaseOrdersTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Check, Eye, Ban } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  warningBg:  '#fef3c7',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const money = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const today = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const Modal = ({ title, subtitle, onClose, width = '760px', children }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: '#ffffff', borderRadius: '14px', width, maxWidth: '100%',
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}
    >
      <div style={{
        padding: '20px 24px', borderBottom: `1px solid ${C.borderSoft}`,
        display: 'flex', alignItems: 'flex-start', gap: '14px',
      }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: C.text }}>{title}</h3>
          {subtitle && <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>{subtitle}</p>}
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', padding: '2px' }}
        >
          <X size={20} />
        </button>
      </div>
      <div style={{ padding: '22px 24px', overflowY: 'auto' }}>{children}</div>
    </div>
  </div>
);

const thStyle = {
  textAlign: 'left', padding: '11px 22px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 22px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};


// ============================================================
// B. STATUS BADGE
// ============================================================

const STATUS_STYLE = {
  pending:   { label: 'Pending',   bg: C.infoBg,    fg: C.info },
  partial:   { label: 'Partial',   bg: C.warningBg, fg: C.warning },
  received:  { label: 'Received',  bg: C.successBg, fg: C.success },
  cancelled: { label: 'Cancelled', bg: C.borderSoft, fg: C.textMuted },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span style={{
      fontSize: '12px', fontWeight: '600', padding: '3px 11px',
      borderRadius: '11px', background: s.bg, color: s.fg,
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
};


// ============================================================
// C. LINE ITEMS EDITOR
//
// Lines live in an array in the parent's form state. Each row
// is edited by index, so adding and removing is just array work.
// ============================================================

const blankLine = { jumbo_id: '', quantity: '', rate: '' };

const LineItemsEditor = ({ items, setItems, jumbos }) => {
  const updateLine = (index, patch) => {
    setItems(items.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const addLine    = () => setItems([...items, { ...blankLine }]);
  const removeLine = (index) => setItems(items.filter((_, i) => i !== index));

  const grandTotal = items.reduce(
    (sum, l) => sum + (Number(l.quantity || 0) * Number(l.rate || 0)), 0
  );

  const cell = { padding: '8px 10px', verticalAlign: 'top' };
  const head = {
    padding: '9px 10px', fontSize: '11px', fontWeight: '600',
    color: C.textSoft, textTransform: 'uppercase', letterSpacing: '0.3px',
    background: C.rowAlt, textAlign: 'left',
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px' }}>
          <thead>
            <tr>
              <th style={{ ...head, width: '44%' }}>Jumbo</th>
              <th style={{ ...head, width: '15%' }}>Qty (rolls)</th>
              <th style={{ ...head, width: '18%' }}>Rate / roll</th>
              <th style={{ ...head, width: '18%', textAlign: 'right' }}>Amount</th>
              <th style={{ ...head, width: '5%' }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((line, index) => {
              const amount = Number(line.quantity || 0) * Number(line.rate || 0);
              const jumbo = jumbos.find(j => String(j.id) === String(line.jumbo_id));

              return (
                <tr key={index} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                  <td style={cell}>
                    <select
                      style={inputStyle}
                      value={line.jumbo_id}
                      onChange={e => updateLine(index, { jumbo_id: e.target.value })}
                    >
                      <option value="">Select jumbo…</option>
                      {jumbos.map(j => (
                        <option key={j.id} value={j.id}>{j.name}</option>
                      ))}
                    </select>
                    {/* Spec reminder, so the buyer sees what they picked */}
                    {jumbo && (
                      <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>
                        {Number(jumbo.default_meters).toLocaleString()} m ·
                        {' '}{jumbo.width_inches}" ·
                        {' '}core {jumbo.core_length_meters} m
                      </div>
                    )}
                  </td>
                  <td style={cell}>
                    <TextInput
                      type="number"
                      value={line.quantity}
                      onChange={e => updateLine(index, { quantity: e.target.value })}
                      placeholder="0"
                    />
                  </td>
                  <td style={cell}>
                    <TextInput
                      type="number" step="0.01"
                      value={line.rate}
                      onChange={e => updateLine(index, { rate: e.target.value })}
                      placeholder="0"
                    />
                  </td>
                  <td style={{ ...cell, textAlign: 'right', paddingTop: '18px', fontWeight: '600' }}>
                    {amount > 0 ? money(amount) : '—'}
                  </td>
                  <td style={{ ...cell, paddingTop: '14px' }}>
                    {/* Last remaining line can't be removed — a PO
                        with no lines is meaningless */}
                    {items.length > 1 && (
                      <IconBtn danger title="Remove line" onClick={() => removeLine(index)}>
                        <Trash2 size={14} />
                      </IconBtn>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{
        padding: '12px 16px', borderTop: `1px solid ${C.borderSoft}`,
        display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        background: C.rowAlt,
      }}>
        <button
          type="button"
          onClick={addLine}
          style={{
            padding: '8px 14px', borderRadius: '7px',
            border: `1px dashed ${C.border}`, background: '#ffffff',
            color: C.textSoft, fontSize: '13px', fontWeight: '600',
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}
        >
          <Plus size={14} /> Add line
        </button>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
          <span style={{ fontSize: '13px', color: C.textSoft, fontWeight: '600' }}>Order total</span>
          <span style={{ fontSize: '20px', fontWeight: '700', color: C.text }}>
            {money(grandTotal)}
          </span>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// D. PO FORM
// ============================================================

const PoForm = ({ form, setForm, vendors, jumbos, onSave, onCancel, saving }) => (
  <div style={{ padding: '20px 22px', background: C.rowAlt, borderBottom: `1px solid ${C.borderSoft}` }}>
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '16px', marginBottom: '20px',
    }}>
      <Field label="Vendor">
        <select
          style={inputStyle}
          value={form.vendor_id}
          onChange={e => setForm({ ...form, vendor_id: e.target.value })}
        >
          <option value="">Select vendor…</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </Field>

      <Field label="PO date">
        <TextInput
          type="date"
          value={form.po_date}
          onChange={e => setForm({ ...form, po_date: e.target.value })}
        />
      </Field>

      <Field label="Notes">
        <TextInput
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Optional"
        />
      </Field>
    </div>

    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block', fontSize: '12px', fontWeight: '600',
        color: C.textSoft, marginBottom: '8px',
      }}>
        Line items
      </label>
      <LineItemsEditor
        items={form.items}
        setItems={items => setForm({ ...form, items })}
        jumbos={jumbos}
      />
    </div>

    <div style={{ display: 'flex', gap: '10px' }}>
      <Btn onClick={onSave} disabled={saving}>
        <Check size={16} /> {saving ? 'Saving…' : (form.id ? 'Update order' : 'Create order')}
      </Btn>
      <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
    </div>
  </div>
);


// ============================================================
// E. DETAIL MODAL
// ============================================================

const PoDetailModal = ({ poId, onClose }) => {
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/tape/purchase-orders/${poId}`, { headers })
      .then(res => setPo(res.data.data))
      .catch(() => setPo(null))
      .finally(() => setLoading(false));
  }, [poId]); // eslint-disable-line

  const cell = { padding: '10px 12px', fontSize: '13px', borderTop: `1px solid ${C.borderSoft}` };
  const head = { ...cell, background: C.rowAlt, fontWeight: '600', color: C.textSoft, fontSize: '11px', textTransform: 'uppercase', borderTop: 'none' };

  return (
    <Modal
      title={po ? po.po_number : 'Purchase order'}
      subtitle={po ? `${po.vendor_name} · ${fmtDate(po.po_date)}` : undefined}
      onClose={onClose}
    >
      {loading && <p style={{ color: C.textSoft }}>Loading…</p>}

      {!loading && po && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '12px', marginBottom: '20px',
          }}>
            {[
              ['Status',   <StatusBadge key="s" status={po.status} />],
              ['Ordered',  `${po.total_ordered} rolls`],
              ['Received', `${po.total_received} rolls`],
              ['Value',    money(po.total_amount)],
            ].map(([label, value]) => (
              <div key={label} style={{ background: C.rowAlt, borderRadius: '8px', padding: '12px 14px' }}>
                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                  {label}
                </div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: C.text, marginTop: '4px' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div style={{ border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
                <thead>
                  <tr>
                    <th style={{ ...head, textAlign: 'left' }}>Jumbo</th>
                    <th style={{ ...head, textAlign: 'right' }}>Ordered</th>
                    <th style={{ ...head, textAlign: 'right' }}>Received</th>
                    <th style={{ ...head, textAlign: 'right' }}>Pending</th>
                    <th style={{ ...head, textAlign: 'right' }}>Rate</th>
                    <th style={{ ...head, textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {po.items.map(item => (
                    <tr key={item.id}>
                      <td style={cell}>
                        <div style={{ fontWeight: '600' }}>{item.jumbo_name}</div>
                        <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>
                          {Number(item.default_meters).toLocaleString()} m · {item.width_inches}"
                        </div>
                      </td>
                      <td style={{ ...cell, textAlign: 'right' }}>{item.quantity}</td>
                      <td style={{ ...cell, textAlign: 'right', color: C.success, fontWeight: '600' }}>
                        {item.received_quantity}
                      </td>
                      <td style={{
                        ...cell, textAlign: 'right', fontWeight: '600',
                        color: item.pending_quantity > 0 ? C.warning : C.textMuted,
                      }}>
                        {item.pending_quantity > 0 ? item.pending_quantity : '—'}
                      </td>
                      <td style={{ ...cell, textAlign: 'right' }}>{money(item.rate)}</td>
                      <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>{money(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {po.notes && (
            <div style={{ marginTop: '16px', fontSize: '13px', color: C.textSoft }}>
              <strong style={{ color: C.text }}>Notes:</strong> {po.notes}
            </div>
          )}
        </>
      )}

      {!loading && !po && <p style={{ color: C.danger }}>Could not load this order.</p>}
    </Modal>
  );
};


// ============================================================
// F. MAIN COMPONENT
// ============================================================

const emptyForm = {
  id: null,
  vendor_id: '',
  po_date: today(),
  notes: '',
  items: [{ ...blankLine }],
};

const PurchaseOrdersTab = () => {
  const [orders, setOrders]   = useState([]);
  const [vendors, setVendors] = useState([]);
  const [jumbos, setJumbos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [banner, setBanner]   = useState({ type: '', message: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const query = statusFilter ? `?status=${statusFilter}` : '';
      const [o, v, j] = await Promise.all([
        axios.get(`${API}/tape/purchase-orders${query}`, { headers }),
        axios.get(`${API}/tape/vendors/active`, { headers }),
        axios.get(`${API}/tape/settings/jumbos/active`, { headers }),
      ]);
      setOrders(o.data.data || []);
      setVendors(v.data.data || []);
      setJumbos(j.data.data || []);
    } catch {
      notify('error', 'Could not load purchase orders. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openEdit = async (row) => {
    // The list rows don't carry line items, so fetch the full PO
    try {
      const res = await axios.get(`${API}/tape/purchase-orders/${row.id}`, { headers });
      const po = res.data.data;
      setForm({
        id: po.id,
        vendor_id: String(po.vendor_id),
        po_date: po.po_date.split('T')[0],
        notes: po.notes || '',
        items: po.items.map(i => ({
          jumbo_id: String(i.jumbo_id),
          quantity: i.quantity,
          rate: i.rate,
        })),
      });
    } catch {
      notify('error', 'Could not open this order');
    }
  };

  const save = async () => {
    const f = form;
    if (!f.vendor_id) return notify('error', 'Pick a vendor');
    if (!f.po_date)   return notify('error', 'PO date is required');

    const validLines = f.items.filter(l => l.jumbo_id && Number(l.quantity) > 0 && Number(l.rate) > 0);
    if (validLines.length === 0) {
      return notify('error', 'Add at least one complete line item');
    }

    setSaving(true);
    try {
      const payload = {
        vendor_id: f.vendor_id,
        po_date: f.po_date,
        notes: f.notes,
        items: validLines,
      };

      if (f.id) {
        await axios.put(`${API}/tape/purchase-orders/${f.id}`, payload, { headers });
        notify('success', 'Purchase order updated');
      } else {
        await axios.post(`${API}/tape/purchase-orders`, payload, { headers });
        notify('success', 'Purchase order created');
      }
      setForm(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const cancelOrder = async (row) => {
    if (!window.confirm(`Cancel ${row.po_number}? No further deliveries can be recorded against it.`)) return;
    try {
      await axios.put(`${API}/tape/purchase-orders/${row.id}/cancel`, {}, { headers });
      notify('success', 'Purchase order cancelled');
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not cancel');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.po_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/tape/purchase-orders/${row.id}`, { headers });
      notify('success', 'Purchase order deleted');
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  // Headline numbers
  const openCount    = orders.filter(o => ['pending', 'partial'].includes(o.status)).length;
  const openValue    = orders
    .filter(o => ['pending', 'partial'].includes(o.status))
    .reduce((sum, o) => sum + Number(o.total_amount || 0), 0);
  const pendingRolls = orders
    .filter(o => ['pending', 'partial'].includes(o.status))
    .reduce((sum, o) => sum + (Number(o.total_ordered) - Number(o.total_received)), 0);

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      {/* Summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        {[
          ['Open orders', openCount, C.text],
          ['Rolls awaited', pendingRolls, pendingRolls > 0 ? C.warning : C.textMuted],
          ['Committed value', money(openValue), C.text],
        ].map(([label, value, color]) => (
          <div key={label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '16px 18px',
          }}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '22px', fontWeight: '700', color, marginTop: '4px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '180px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Purchase orders
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Status updates itself as deliveries are recorded.
            </p>
          </div>

          {/* Status filter */}
          <select
            style={{ ...inputStyle, width: 'auto', minWidth: '150px' }}
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {!form && (
            <Btn onClick={() => setForm(emptyForm)}>
              <Plus size={16} /> New order
            </Btn>
          )}
        </div>

        {form && (
          <PoForm
            form={form}
            setForm={setForm}
            vendors={vendors}
            jumbos={jumbos}
            onSave={save}
            onCancel={() => setForm(null)}
            saving={saving}
          />
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>PO number</th>
                <th style={thStyle}>Vendor</th>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Lines</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Received</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Value</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && orders.map(row => {
                const ordered  = Number(row.total_ordered);
                const received = Number(row.total_received);
                const canEdit  = received === 0 && row.status !== 'cancelled';

                return (
                  <tr key={row.id}>
                    <td style={{ ...tdStyle, fontWeight: '700' }}>{row.po_number}</td>
                    <td style={tdStyle}>{row.vendor_name}</td>
                    <td style={{ ...tdStyle, color: C.textSoft }}>{fmtDate(row.po_date)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>{row.line_count}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <span style={{ fontWeight: '600', color: received === ordered ? C.success : C.text }}>
                        {received}
                      </span>
                      <span style={{ color: C.textMuted }}> / {ordered}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                      {money(row.total_amount)}
                    </td>
                    <td style={tdStyle}><StatusBadge status={row.status} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '7px' }}>
                        <IconBtn title="View details" onClick={() => setDetailId(row.id)}>
                          <Eye size={15} />
                        </IconBtn>
                        {canEdit && (
                          <IconBtn title="Edit" onClick={() => openEdit(row)}>
                            <Pencil size={15} />
                          </IconBtn>
                        )}
                        {row.status !== 'cancelled' && row.status !== 'received' && (
                          <IconBtn title="Cancel order" onClick={() => cancelOrder(row)}>
                            <Ban size={15} />
                          </IconBtn>
                        )}
                        {received === 0 && (
                          <IconBtn danger title="Delete" onClick={() => remove(row)}>
                            <Trash2 size={15} />
                          </IconBtn>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && orders.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No purchase orders yet. Click "New order" to raise the first one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detailId && (
        <PoDetailModal poId={detailId} onClose={() => setDetailId(null)} />
      )}
    </div>
  );
};

export default PurchaseOrdersTab;